import { useState } from 'react';
import { sendFormEmail } from '../utils/emailService';

const hearAboutOptions = ['Select an option', 'Search Engine', 'LinkedIn', 'Instagram', 'Other'];

function EarlyAccessModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    country: '',
    devices: '',
    hearAbout: '',
    query: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const validateEmail = (email) => {
    const freeProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    if (freeProviders.includes(domain)) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please use a company email address';
    }
    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.devices) {
      newErrors.devices = 'Please specify the number of devices';
    } else if (parseInt(formData.devices) < 1 || parseInt(formData.devices) > 20) {
      newErrors.devices = 'Device count must be between 1 and 20';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'devices') {
      const num = value === '' ? '' : Math.min(20, Math.max(0, parseInt(value) || 0));
      setFormData(prev => ({ ...prev, [name]: num.toString() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSending(true);
    setSendError('');
    try {
      await sendFormEmail(formData, true);
      setSubmitted(true);
    } catch (err) {
      setSendError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '', company: '', jobTitle: '', phone: '', country: '', devices: '', hearAbout: '', query: '' });
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="ea-modal-overlay" onClick={handleClose}>
      <div className="ea-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ea-modal-close" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>

        {submitted ? (
          <div className="ea-modal-success">
            <div className="ea-success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Application Received</h3>
            <p>Thank you, {formData.firstName}! We've received your early access request. Our team will review your application and get back to you at <strong>{formData.email}</strong> shortly.</p>
            <button className="btn-primary-gradient" onClick={handleClose} style={{ marginTop: '20px' }}>Done</button>
          </div>
        ) : (
          <>
            <div className="ea-modal-header">
              <span className="ea-modal-badge">Launching July–August 2026 — Limited to 5 Pilot Customers</span>
              <h2>Apply for Early Access</h2>
              <p>Be among the first to access Netwatch when we launch. Help shape the future of network monitoring.</p>
            </div>

            <form onSubmit={handleSubmit} className="ea-modal-form">
              <div className="ea-form-row">
                <div className="ea-form-group">
                  <label htmlFor="ea-firstName">First Name <span className="ea-required">*</span></label>
                  <input
                    id="ea-firstName"
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? 'ea-input-error' : ''}
                  />
                  {errors.firstName && <span className="ea-error">{errors.firstName}</span>}
                </div>

                <div className="ea-form-group">
                  <label htmlFor="ea-lastName">Last Name <span className="ea-required">*</span></label>
                  <input
                    id="ea-lastName"
                    type="text"
                    name="lastName"
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? 'ea-input-error' : ''}
                  />
                  {errors.lastName && <span className="ea-error">{errors.lastName}</span>}
                </div>
              </div>

              <div className="ea-form-row">
                <div className="ea-form-group">
                  <label htmlFor="ea-email">Work Email <span className="ea-required">*</span></label>
                  <input
                    id="ea-email"
                    type="email"
                    name="email"
                    placeholder="john@acmecorp.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'ea-input-error' : ''}
                  />
                  {errors.email && <span className="ea-error">{errors.email}</span>}
                </div>

                <div className="ea-form-group">
                  <label htmlFor="ea-company">Company <span className="ea-required">*</span></label>
                  <input
                    id="ea-company"
                    type="text"
                    name="company"
                    placeholder="Acme Corp"
                    value={formData.company}
                    onChange={handleChange}
                    className={errors.company ? 'ea-input-error' : ''}
                  />
                  {errors.company && <span className="ea-error">{errors.company}</span>}
                </div>
              </div>

              <div className="ea-form-row">
                <div className="ea-form-group">
                  <label htmlFor="ea-jobTitle">Job Title <span className="ea-required">*</span></label>
                  <input
                    id="ea-jobTitle"
                    type="text"
                    name="jobTitle"
                    placeholder="Network Engineer"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className={errors.jobTitle ? 'ea-input-error' : ''}
                  />
                  {errors.jobTitle && <span className="ea-error">{errors.jobTitle}</span>}
                </div>

                <div className="ea-form-group">
                  <label htmlFor="ea-phone">Phone <span className="ea-required">*</span></label>
                  <input
                    id="ea-phone"
                    type="tel"
                    name="phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'ea-input-error' : ''}
                  />
                  {errors.phone && <span className="ea-error">{errors.phone}</span>}
                </div>
              </div>

              <div className="ea-form-row">
                <div className="ea-form-group">
                  <label htmlFor="ea-country">Country <span className="ea-required">*</span></label>
                  <input
                    id="ea-country"
                    type="text"
                    name="country"
                    placeholder="United States"
                    value={formData.country}
                    onChange={handleChange}
                    className={errors.country ? 'ea-input-error' : ''}
                  />
                  {errors.country && <span className="ea-error">{errors.country}</span>}
                </div>

                <div className="ea-form-group">
                  <label htmlFor="ea-hearAbout">How did you hear about us?</label>
                  <select
                    id="ea-hearAbout"
                    name="hearAbout"
                    value={formData.hearAbout}
                    onChange={handleChange}
                  >
                    {hearAboutOptions.map((opt, i) => (
                      <option key={i} value={i === 0 ? '' : opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ea-form-row">
                <div className="ea-form-group">
                  <label htmlFor="ea-devices">Devices to Monitor <span className="ea-required">*</span> <span className="ea-hint">(max 20)</span></label>
                  <input
                    id="ea-devices"
                    type="number"
                    name="devices"
                    min="1"
                    max="20"
                    placeholder="e.g. 10"
                    value={formData.devices}
                    onChange={handleChange}
                    className={errors.devices ? 'ea-input-error' : ''}
                  />
                  {errors.devices && <span className="ea-error">{errors.devices}</span>}
                </div>
                <div className="ea-form-group"></div>
              </div>

              <div className="ea-form-group">
                <label htmlFor="ea-query">Tell us about your use case <span className="ea-hint">(optional)</span></label>
                <textarea
                  id="ea-query"
                  name="query"
                  rows="3"
                  placeholder="What challenges are you looking to solve with Netwatch?"
                  value={formData.query}
                  onChange={handleChange}
                ></textarea>
              </div>

              {sendError && (
                <div style={{ color: '#ef4444', fontSize: '14px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px', marginBottom: '12px' }}>{sendError}</div>
              )}
              <button type="submit" className="btn-primary-gradient ea-submit-btn" disabled={sending}>
                {sending ? 'Sending...' : 'Submit Application'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default EarlyAccessModal;
